import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiCheck, FiUser, FiBookOpen, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import versionService from '../../services/versionService';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import { hasCheckedInRecord } from '../../utils/attendanceSessionStatus';
import styles from './ActiveAttendanceSessions.module.css';

// Helper to detect if an error is likely due to a deployment
const isDeploymentRelatedError = (error) => {
  if (!error) return false;

  // Check for common deployment-related error patterns
  const errorMessage = error.message || error.toString() || '';
  const errorStatus = error.status || error.response?.status;

  // 503 Service Unavailable - common during deployments
  if (errorStatus === 503) return true;

  // Network errors during deployment
  if (errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Network request failed')) {
    return true;
  }

  // HTML returned instead of JSON (server restarting, returning error page)
  if (errorMessage.includes('Unexpected token') ||
      errorMessage.includes('SyntaxError') ||
      errorMessage.includes('<!DOCTYPE') ||
      errorMessage.includes('<html')) {
    return true;
  }

  // Chunk loading errors
  if (errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError')) {
    return true;
  }

  return false;
};

function ActiveAttendanceSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const deploymentErrorCountRef = useRef(0);
  const pollingPausedRef = useRef(false);

  // Check if an update is available when deployment-related errors occur
  const checkForUpdate = useCallback(async () => {
    try {
      const hasUpdate = await versionService.checkForUpdates();
      if (hasUpdate) {
        setUpdateAvailable(true);
        pollingPausedRef.current = true; // Stop polling when update is detected
        logger.info('App update detected during attendance fetch - pausing polling');
      }
      return hasUpdate;
    } catch (e) {
      logger.warn('Failed to check for updates', { error: e });
      return false;
    }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    // Don't fetch if user is null or not a student
    if (!user || user.role !== 'student') {
      setSessions([]);
      return;
    }

    // Don't fetch if polling is paused due to detected update
    if (pollingPausedRef.current) {
      logger.debug('Polling paused - update available');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Always add cache-busting parameter to ensure fresh data
      const endpoint = '/attendance/active-today?v=' + Date.now();
      const response = await apiService.request(endpoint);

      console.log('📍 Active Sessions API Response:', response);

      if (response.success) {
        setSessions(response.sessions || []);
        console.log('📍 Sessions set:', response.sessions?.length || 0, 'sessions');
        // Reset error count on successful fetch
        deploymentErrorCountRef.current = 0;
      } else {
        setError(response.message || 'Failed to fetch active sessions');
        console.error('📍 API Error:', response.message);
      }
    } catch (error) {
      // Handle permission errors gracefully - don't show error for non-students
      if (error.status === 403) {
        logger.debug('User does not have permission to access attendance sessions');
        setSessions([]);
        setError('');
        return;
      }

      // Check if this might be a deployment-related error
      if (isDeploymentRelatedError(error)) {
        deploymentErrorCountRef.current += 1;
        logger.warn('Possible deployment-related error in attendance fetch', {
          errorCount: deploymentErrorCountRef.current,
          error: error.message
        });

        // After 2 consecutive deployment-like errors, check for updates
        if (deploymentErrorCountRef.current >= 2) {
          const hasUpdate = await checkForUpdate();
          if (hasUpdate) {
            setError('');
            return; // Don't show error, update banner will be shown
          }
        }

        // Show a more helpful error message for deployment scenarios
        if (deploymentErrorCountRef.current >= 3) {
          setError('Connection interrupted. The app may be updating - please refresh the page.');
        } else {
          // Don't show error for first couple of failures - might be transient
          setError('');
        }
      } else {
        setError('Failed to fetch active sessions. Please try again.');
        deploymentErrorCountRef.current = 0;
      }
    } finally {
      setLoading(false);
    }
  }, [user, checkForUpdate]);


  useEffect(() => {
    if (user) {
      // Always fetch fresh data when component mounts
      fetchActiveSessions();
      
      // Refresh when user returns to the tab (focus event)
      const handleFocus = () => {
        logger.debug('Page focused - refreshing sessions');
        fetchActiveSessions();
      };
      
      // Refresh when page becomes visible (visibilitychange event)
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          logger.debug('Page visible - refreshing sessions');
          fetchActiveSessions();
        }
      };
      
      // Periodic refresh every 2 minutes to catch admin updates
      const interval = setInterval(() => {
        logger.debug('Periodic refresh - checking for session updates');
        fetchActiveSessions();
      }, 120000); // 2 minutes
      
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // Clear sessions when user is null
      setSessions([]);
    }
  }, [user, fetchActiveSessions]);



  // Filter sessions to show until check-in window ends
  const getVisibleSessions = () => {
    if (!sessions || sessions.length === 0) {
      console.log('📍 No sessions available');
      return [];
    }

    const now = DateTime.now().setZone('America/Chicago');
    console.log('📍 Current time:', now.toISO());
    console.log('📍 Total sessions:', sessions.length);

    const visibleSessions = sessions.filter(session => {
      if (!session.end_time) {
        console.log('📍 Session filtered (no end_time):', session.session_id);
        return false;
      }

      // Parse the end_time to determine when check-in closes
      let endTime;
      try {
        if (typeof session.end_time === 'string') {
          endTime = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
        } else {
          endTime = DateTime.fromJSDate(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
        }
      } catch (e) {
        console.log('📍 Session filtered (parse error):', session.session_id, e.message);
        return false;
      }

      if (!endTime.isValid) {
        console.log('📍 Session filtered (invalid endTime):', session.session_id);
        return false;
      }

      const isVisible = now <= endTime;
      console.log('📍 Session:', session.session_id, 'end_time:', endTime.toISO(), 'visible:', isVisible);
      return isVisible;
    });

    console.log('📍 Visible sessions:', visibleSessions.length);
    return visibleSessions;
  };

  const handleCheckIn = async (sessionId) => {
    try {
      setCheckingIn(prev => new Set(prev).add(sessionId));
      setError('');
      setSuccess('');

      const response = await apiService.checkInAttendance(sessionId, 'present');

      if (response.success) {
        setSuccess('Successfully checked in for attendance!');

        // Refresh sessions to get the latest data from the server
        logger.debug('Check-in successful - refreshing sessions');
        await fetchActiveSessions();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Use the specific error message from the API response
        setError(response.message || response.error || 'Failed to check in for attendance');
      }
    } catch (error) {
      logger.error('Error checking in', { error, sessionId });
      // Try to extract meaningful error message from the error response
      let errorMessage = 'Failed to check in for attendance. Please try again.';

      // First, try to get message from response object
      if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.response?.error) {
        errorMessage = error.response.error;
      } else if (error?.message) {
        // If the message looks like JSON, try to parse it
        if (typeof error.message === 'string' && error.message.includes('{')) {
          try {
            const parsed = JSON.parse(error.message);
            errorMessage = parsed.message || parsed.error || error.message;
          } catch (e) {
            // If parsing fails, use the original message
            errorMessage = error.message;
          }
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setCheckingIn(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };




  // Helper function to get session status
  const getSessionStatus = (session) => {
    // Checked-in state should always win, even after window closes
    if (hasCheckedInRecord(session)) {
      return 'checked-in';
    }

    // Show closed state only for students who did not check in
    if (
      session?.isPast ||
      session?.status === 'ended' ||
      session?.status === 'closed' ||
      session?.current_status === 'ended' ||
      session?.current_status === 'closed'
    ) {
      return 'ended';
    }

    return 'available';
  };

  const getCheckedInTimeLabel = (session) => {
    const source = session.checked_in_at_utc || session.last_checkin;
    if (!source) return null;

    try {
      let checkedInTime;
      if (typeof source === 'string') {
        checkedInTime = DateTime.fromISO(source, { zone: 'utc' }).setZone('America/Chicago');
      } else {
        checkedInTime = DateTime.fromJSDate(source, { zone: 'utc' }).setZone('America/Chicago');
      }

      if (checkedInTime.isValid) {
        return checkedInTime.toFormat('h:mm a');
      }
    } catch (e) {
      logger.warn('Failed to parse check-in time', { source, error: e.message });
    }

    return null;
  };

  const extractWeekNumber = (title) => {
    if (!title) return null;
    const match = String(title).match(/week\s*#?\s*(\d+)/i);
    return match ? Number(match[1]) : null;
  };

  const getTimeRangeLabel = (session) => {
    if (!session.start_time || !session.end_time) return 'In progress now';
    const start = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
    const end = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
    if (!start.isValid || !end.isValid) return 'In progress now';
    return `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`;
  };

  const isCheckInWindowActive = (session) => {
    if (!session.start_time || !session.end_time) return false;
    const now = DateTime.now().setZone('America/Chicago');
    const start = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
    const end = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
    if (!start.isValid || !end.isValid) return false;
    return now >= start && now <= end;
  };

  const isLiveSessionActive = (session) => {
    if (!session.virtual_start_time || !session.virtual_end_time) return false;
    const now = DateTime.now().setZone('America/Chicago');
    const start = DateTime.fromFormat(String(session.virtual_start_time).slice(0, 5), 'HH:mm', { zone: 'America/Chicago' });
    const end = DateTime.fromFormat(String(session.virtual_end_time).slice(0, 5), 'HH:mm', { zone: 'America/Chicago' });
    if (!start.isValid || !end.isValid) return false;
    const startToday = now.set({ hour: start.hour, minute: start.minute, second: 0, millisecond: 0 });
    let endToday = now.set({ hour: end.hour, minute: end.minute, second: 0, millisecond: 0 });
    if (endToday <= startToday) endToday = endToday.plus({ days: 1 });
    return now >= startToday && now <= endToday;
  };

  const getVirtualTimeRangeLabel = (session) => {
    const formatTime = (value) => {
      if (!value) return null;
      const parsed = DateTime.fromFormat(String(value).slice(0, 5), 'HH:mm');
      return parsed.isValid ? parsed.toFormat('h:mm a') : null;
    };
    const start = formatTime(session.virtual_start_time);
    const end = formatTime(session.virtual_end_time);
    if (start && end) return `${start} – ${end}`;
    if (start) return `Starts ${start}`;
    if (end) return `Ends ${end}`;
    return 'In progress now';
  };

  if (loading && sessions.length === 0) {
    return (
      <div className={styles.attendanceCard}>
        <div className={styles.attendanceHeader}>
          <h2 className={styles.attendanceTitle}>
            <FiClock size={20} />
            Active Attendance Sessions
          </h2>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Loading active sessions...</span>
        </div>
      </div>
    );
  }

  // Don't show the component at all if there are no sessions or no visible sessions
  if (sessions.length === 0 || getVisibleSessions().length === 0) {
    return null;
  }

  return (
    <div className={styles.attendanceCard}>
      <div className={styles.attendanceHeader}>
        <h2 className={styles.attendanceTitle}>
          <FiClock size={20} />
          My Active Sessions
        </h2>
      </div>

      <div className={styles.attendanceList}>
        {/* Update available banner */}
        {updateAvailable && (
          <motion.div
            className={styles.updateBanner}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={styles.updateBannerContent}>
              <FiRefreshCw size={16} />
              <span>A new version is available. Please refresh to continue.</span>
            </div>
            <button
              className={styles.updateRefreshButton}
              onClick={() => window.location.reload(true)}
            >
              Refresh Now
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            className={styles.successMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiCheck size={16} />
            {success}
          </motion.div>
        )}

        {error && !updateAvailable && (
          <motion.div
            className={styles.errorMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiAlertCircle size={16} />
            {error}
          </motion.div>
        )}

        {getVisibleSessions().map((session, index) => {
          const sessionStatus = getSessionStatus(session);
          const isCheckedIn = sessionStatus === 'checked-in';
          const isEnded = sessionStatus === 'ended';

          return (
            <motion.div
              key={session.session_id}
              className={styles.sessionGrid}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              {/* Combined Session Card */}
              <div className={`${styles.sessionCard} ${styles.sessionCard__checkin} ${isCheckedIn ? styles['sessionCard__checkin--completed'] : ''}`}>
                {/* Card Header */}
                <div className={`${styles.cardHeader} ${styles.cardHeader__checkin} ${isCheckedIn ? styles['cardHeader__checkin--completed'] : ''}`}>
                  Session{extractWeekNumber(session.title) ? ` · Week ${extractWeekNumber(session.title)}` : ''}
                </div>

                {/* Course Info */}
                <div className={styles.sessionContent}>
                  <div className={styles.courseInfo}>
                    <div className={styles.courseNameRow}>
                      <FiBookOpen size={16} className={styles.courseIcon} />
                      <span className={styles.courseName}>
                        {session.course_code || session.elective_code} · {session.course_name || session.elective_name}
                      </span>
                    </div>
                  </div>
                  <div className={styles.instructorInfo}>
                    <FiUser size={14} className={styles.instructorIcon} />
                    <span className={styles.instructorName}>
                      {session.entity_type === 'elective' && session.instructors ? session.instructors : `${session.instructor_first_name} ${session.instructor_last_name}`}
                    </span>
                  </div>
                </div>

                {/* Stacked sections: Virtual Session + Check-In Window */}
                <div className={styles.combinedSections}>
                  {/* Virtual Session row */}
                  {/* Check-In Window row */}
                  <div className={styles.combinedSection}>
                    <div className={styles.combinedSectionLeft}>
                      <div className={styles.sessionTypeWrapper}>
                        <div className={`${styles.sessionTypeLabel} ${styles.label__checkin} ${isCheckedIn ? styles['label__checkin--completed'] : ''}`}>
                          CHECK-IN WINDOW
                        </div>
                        {isCheckInWindowActive(session) && (
                          <div className={`${styles.activeLabel} ${styles.activeBadge__checkin} ${isCheckedIn ? styles['activeBadge__checkin--completed'] : ''}`}>
                            ACTIVE
                          </div>
                        )}
                      </div>
                      <div className={`${styles.timeDisplay} ${styles.time__checkin} ${isCheckedIn ? styles['time__checkin--completed'] : ''}`}>
                        {getTimeRangeLabel(session)}
                      </div>
                    </div>
                    <div className={styles.combinedSectionRight}>
                      {(() => {
                        if (isCheckedIn) {
                          const checkedInTime = getCheckedInTimeLabel(session);
                          return (
                            <button
                              className={`${styles.actionButton} ${styles.actionButton__checkedIn}`}
                              disabled={true}
                            >
                              Checked In{checkedInTime && ` at ${checkedInTime}`}
                            </button>
                          );
                        }
                        if (isEnded) {
                          return (
                            <button
                              className={`${styles.actionButton} ${styles.actionButton__closed}`}
                              disabled={true}
                            >
                              Closed
                            </button>
                          );
                        }
                        return (
                          <button
                            className={`${styles.actionButton} ${styles.actionButton__checkin}`}
                            onClick={() => handleCheckIn(session.session_id)}
                            disabled={checkingIn.has(session.session_id)}
                          >
                            {checkingIn.has(session.session_id) ? (
                              <div className={styles.loadingSpinner} />
                            ) : (
                              'Complete Check-In'
                            )}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Virtual Session row */}
                  {session.virtual_link && (
                    <>
                      <hr className={styles.combinedSectionDivider} />
                      <div className={styles.combinedSection}>
                        <div className={styles.combinedSectionLeft}>
                          <div className={styles.sessionTypeWrapper}>
                            <div className={`${styles.sessionTypeLabel} ${styles.label__live}`}>
                              VIRTUAL SESSION
                            </div>
                            {isLiveSessionActive(session) && (
                              <div className={`${styles.activeLabel} ${styles.activeBadge__live}`}>
                                ACTIVE
                              </div>
                            )}
                          </div>
                          <div className={`${styles.timeDisplay} ${styles.time__live}`}>
                            {getVirtualTimeRangeLabel(session)}
                          </div>
                        </div>
                        <div className={styles.combinedSectionRight}>
                          <a
                            className={`${styles.actionButton} ${styles.actionButton__live} ${styles.actionButton__noHover}`}
                            href={session.virtual_link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Join Teams Meeting
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default ActiveAttendanceSessions;
